import { css } from '@emotion/react';

import * as React from 'react';

import { compose } from 'recompose';
import connectToStores from 'vendor/cnpm/fluxible.v0-4/addons/connectToStores';

import Retracked from 'js/app/retracked';
import { isAuthenticatedUser } from 'js/lib/user';

import { Grid } from '@coursera/cds-core';

import DefaultHeader from 'bundles/composable-page-header-consumer/header-renderers/DefaultHeader';
import UniversalPageFeatures from 'bundles/composable-page-header/components/UniversalPageFeatures';
import PageFooter from 'bundles/page-footer/components/PageFooter';
import type { Stores } from 'bundles/unified-career-academy/appEnv';
import CoachChatPathways from 'bundles/unified-common/components/CoachChatPathways';
import InteractionGuard from 'bundles/unified-common/components/InteractionGuard';
import GlobalCoachChat from 'bundles/unified-common/components/coach-on-discovery/GlobalCoachChat';
import { useCoachState } from 'bundles/unified-common/providers/CoachStateProvider';
import {
  GlobalCoachChatStateProvider,
  useGlobalCoachChatState,
} from 'bundles/unified-common/providers/GlobalCoachChatStateProvider';
import NavAiSparkleExperimentProvider, {
  useNavAiSparkle,
} from 'bundles/unified-common/providers/NavAiSparkleExperimentProvider';
import UnifiedAppCheckProvider from 'bundles/unified-common/providers/UnifiedAppCheckProvider';
import withMarketingConsent from 'bundles/user-consent/components/withMarketingConsent';

type GridProps = React.ComponentProps<typeof Grid>;

type Props = {
  requestCountryCode?: string | null;
  csrfToken?: string | null;
};

const UnifiedCareerAcademyHeaderFooterWrapper: React.LegacyFunctionComponentWithChildren<Props> = ({ children }) => {
  const headerRef = React.useRef<HTMLSpanElement>(null); // Declare headerRef variable
  const footerRef = React.useRef<HTMLDivElement>(null);
  const { coachOpen } = useCoachState();
  const { pageChatType, setPageChatType } = useGlobalCoachChatState();
  const { showNavAiSparkle } = useNavAiSparkle();
  const isFullscreenCoachChatOpen = coachOpen && pageChatType === null;

  React.useEffect(() => {
    if (!showNavAiSparkle && isAuthenticatedUser()) {
      setPageChatType('side-by-side');
    }
    return () => setPageChatType(null);
  }, [setPageChatType, showNavAiSparkle]);

  // the grid will need to adjust and respond appropriately to the coach being open to give
  // space for the rest of the page content.
  const defaultGridProps: GridProps = { xs: 12 };

  const layoutGridItemCoachOpenProps: (typeof Grid)['defaultProps'] =
    coachOpen && pageChatType === 'side-by-side'
      ? {
          xs: 12,
          sm: 7,
          md: 8,
          lg: 9,
          xl: 10,
          css: css`
            /* Coach chat takes up 400px width */
            max-width: calc(100% - 400px);
          `,
        }
      : {};

  return (
    <UnifiedAppCheckProvider>
      <DefaultHeader />
      <UniversalPageFeatures />
      <Grid container>
        <Grid item {...layoutGridItemCoachOpenProps} {...defaultGridProps}>
          <InteractionGuard as="main" preventInteraction={isFullscreenCoachChatOpen}>
            {children}
          </InteractionGuard>
        </Grid>
        {coachOpen && pageChatType === 'side-by-side' && (
          <Grid item>
            <CoachChatPathways headerRef={headerRef} footerRef={footerRef} />
          </Grid>
        )}
        {pageChatType === null && <GlobalCoachChat />}
      </Grid>
      <InteractionGuard preventInteraction={isFullscreenCoachChatOpen}>
        <PageFooter elRef={footerRef} />
      </InteractionGuard>
    </UnifiedAppCheckProvider>
  );
};

const UnifiedCareerAcademyApp: React.LegacyFunctionComponentWithChildren<Props> = ({ children }) => {
  return (
    <div className="rc-UnifiedCareerAcademyApp">
      <NavAiSparkleExperimentProvider>
        <GlobalCoachChatStateProvider>
          <UnifiedCareerAcademyHeaderFooterWrapper>{children}</UnifiedCareerAcademyHeaderFooterWrapper>
        </GlobalCoachChatStateProvider>
      </NavAiSparkleExperimentProvider>
    </div>
  );
};

export default compose<React.PropsWithChildren<{}>, {}>(
  Retracked.createTrackedContainer(() => ({
    namespace: {
      app: 'unified_career_academy',
      page: 'consumer_career_academy',
    },
  })),
  connectToStores<Props, {}, Stores>(['ApplicationStore'], ({ ApplicationStore }, props) => ({
    ...props,
    csrfToken: ApplicationStore.getState().csrfToken,
    requestCountryCode: ApplicationStore.getState().requestCountryCode,
  })),
  withMarketingConsent
)(UnifiedCareerAcademyApp);
